import {
  DropdownButton,
  InputRichText,
  SelectionList,
  Text,
  Tooltip,
} from '@dixa/lib-shared-habla';
import { convertChannelCasing } from 'lib-shared-api-utils';
import { Channel } from 'lib-shared-types-channels';
import { useCallback, useContext, useMemo, useRef, useState } from 'react';
import { useUnmount } from 'react-use';

import { aiApi } from 'apis/ai';
import { ConversationContext } from 'components/conversation';
import { copilotUtils } from 'utils/copilot';
import { useCopilotTrial } from 'utils/useCopilotTrial';
import { useEditorContext } from 'utils/useEditorContext';

import { PinnedActionProps } from '../copilotPinnedActions.types';

import { UIBlockerError } from './uiBlockerError';
import { UIBlockerLoading } from './uiBlockerLoading';

export const ImproveReply = ({
  channel,
  text,
  label,
  disabledImprovements,
}: PinnedActionProps): JSX.Element | null => {
  /**
   * All improve reply features available from the start. Some of them might be
   * disabled based on the context. For instance in compose modal, match tone
   * should not be available.
   */
  const improvements = [
    {
      value: 'repeat-input',
      copy: 'Apply writing style guide',
    },
    {
      value: 'expand_reply',
      copy: 'Expand',
    },
    {
      value: 'make_concise',
      copy: 'Shorten',
    },
    {
      value: 'match_tone',
      copy: 'Match tone',
    },
    {
      value: 'make_formal',
      copy: 'Make formal',
    },
    {
      value: 'make_friendly',
      copy: 'Make friendly',
    },
  ];

  const { content } = useContext(InputRichText.Context);
  const { replace } = useEditorContext();
  const { hasCopilot } = useCopilotTrial();
  const triggerRef = useRef<ReturnType<typeof improveReply> | null>(null);
  const [isOpen, setIsOpen] = useState(false);

  const [improveReply, { status }] =
    aiApi.useCreateAiWritingImprovementMutation();

  const isLoading = status === 'pending';
  const { isDisabled, tooltipContent } = copilotUtils.improveReply.getStatus({
    text,
    isLoading,
    label,
  });

  const { conversation } = useContext(ConversationContext);

  const lastMessage = useMemo(() => {
    const events = conversation?.events || [];
    const messages = events.filter(
      (event) =>
        event.type === 'conversation:message_added' &&
        event.direction === 'inbound',
    );

    if (messages.length === 0) return '';
    if (!messages[messages.length - 1].text) return '';
    return messages[messages.length - 1].text as string;
  }, [conversation]);

  useUnmount(() => {
    if (triggerRef.current) {
      triggerRef.current.abort();
    }
  });

  const onClickHandler = useCallback(
    ({ selectedValues }) => {
      setIsOpen(false);

      content.setUiBlocker(
        <UIBlockerLoading loadingText={label ?? 'Improving reply'} />,
      );

      const shouldAddContext =
        selectedValues[0] === 'repeat-input' ||
        selectedValues[0] === 'expand_reply';

      const trigger = improveReply({
        lastMessage,
        text,
        promptName: selectedValues[0],
        contexts: shouldAddContext
          ? {
              features: 'impr_reply',
              ...(channel && {
                channels: convertChannelCasing(channel, 'snake') as Channel,
              }),
            }
          : undefined,
        csid: conversation?.id?.toString(),
      });

      triggerRef.current = trigger;

      trigger
        .unwrap()
        .then((data) => {
          triggerRef.current = null;
          if (data.text) {
            replace(data?.text ?? '');
          }
          content.setUiBlocker(null);
        })
        .catch(() => {
          triggerRef.current = null;
          content.setUiBlocker(
            <UIBlockerError
              onClick={() => {
                content.setUiBlocker(null);
              }}
              errorText={
                label ? `Failed to ${label}` : 'Failed to improve reply'
              }
            />,
          );
        });
    },
    [
      content,
      label,
      improveReply,
      lastMessage,
      text,
      channel,
      conversation?.id,
      replace,
    ],
  );

  const handleOnToggle = (isOpen) => {
    setIsOpen(isOpen);
  };

  const renderImprovement = (
    value: string,
    copy: string,
    isDisabled: boolean,
  ): JSX.Element => {
    return (
      <SelectionList.Item key={value} value={value} isDisabled={isDisabled}>
        <Text.Paragraph>{copy}</Text.Paragraph>
      </SelectionList.Item>
    );
  };

  if (!hasCopilot) {
    return null;
  }

  return (
    <DropdownButton
      dropdown={
        <SelectionList onChange={onClickHandler}>
          {improvements.map((improvement) => {
            const { value, copy } = improvement;

            const disabledImprovement = disabledImprovements?.find(
              (disabledImprovement) => disabledImprovement === value,
            );

            const isDisabled = !!disabledImprovement;

            if (isDisabled) {
              return renderImprovement(value, copy, true);
            }
            return renderImprovement(value, copy, false);
          })}
        </SelectionList>
      }
      fixed
      noStyle
      noArrow
      onToggle={handleOnToggle}
      show={isOpen}
    >
      <Tooltip content={tooltipContent}>
        <InputRichText.ToolbarButton icon="pen" disabled={isDisabled} />
      </Tooltip>
    </DropdownButton>
  );
};
